import { ProjectStatus, ProjectPriority } from '../ProjectStatusIndicator';
import {
  StatusGroup,
  formatRevisionLimit,
  getStatusGroup,
  resolveRevisionLimit,
} from '../../utils/projectStatus';

/**
 * Rules for the creator's project list (MyProjects): which row actions a
 * project allows, the status filters, search and sorting. No React here, so
 * the rules can be tested on their own.
 */

export interface ProjectObject {
  id: string;
  projectName: string;
  status: ProjectStatus | string; // Allow both enum and legacy string values
  videoId: string;
  videoName: string;
  userId?: string; // Project owner ID
  priority?: ProjectPriority | string | null;
  statusHistory?: Array<{
    status: ProjectStatus;
    timestamp: string;
    userId: string;
    comments?: string;
    previousStatus?: ProjectStatus;
  }>;
  createdAt?: string;
  updatedAt?: string;
  editorId?: string | null;
  editorName?: string | null;
  editorEmail?: string | null;
  revisionCount?: number | null;
  revisionLimit?: number | null; // Owner's plan allowance; null from older backends
  editor?: {
    id: string;
    email: string;
    name: string;
    status: string;
  };
}

export interface EditorOption {
  id: string;
  email: string;
  name: string;
  status: string;
}

// ---- Permissions -------------------------------------------------------------

/** The editor fields a project may carry, from either the flat or the nested shape. */
export const projectEditor = (project: ProjectObject) => ({
  id: project.editorId || project.editor?.id || '',
  name: project.editorName || project.editor?.name || '',
  email: project.editorEmail || project.editor?.email || '',
});

export const projectHasEditor = (project: ProjectObject): boolean => {
  const editor = projectEditor(project);
  return Boolean(editor.id || editor.name || editor.email);
};

/** Which row actions this person may take on a project. */
export const getAllowedActions = (project: ProjectObject, currentUser: any) => {
  const status = project.status?.toUpperCase();
  const hasEditor = projectHasEditor(project);
  const revisionCount = project.revisionCount || 0;
  const maxRevisions = resolveRevisionLimit(project.revisionLimit);
  const userRole = currentUser?.role || '';
  const isProjectOwner = currentUser?.uid && project.userId === currentUser.uid;
  const isAssignedEditor = currentUser?.uid && project.editorId === currentUser.uid;

  const allowedActions = {
    canEdit: false,
    canAssignEditor: false,
    canCancel: false,
    canViewDetails: true, // Always allow viewing details
    canRequestRevision: false,
    revisionLimitReached: revisionCount >= maxRevisions,
  };

  // Only allow editing if project is in DRAFT/NEW status AND user is the owner.
  // After submission, editing is restricted.
  if (isProjectOwner && (!status || status === 'DRAFT' || status === 'NEW')) {
    allowedActions.canEdit = true;
  }

  // Assigning an editor needs no editor on the project, and either a
  // superadmin or the owner of a project still at VIDEO_SUBMITTED (most
  // projects get their editor when they are created).
  if (!hasEditor && userRole === 'superadmin') {
    allowedActions.canAssignEditor = true;
  } else if (!hasEditor && isProjectOwner && status === 'VIDEO_SUBMITTED') {
    allowedActions.canAssignEditor = true;
  }

  // Owners and superadmins can cancel a project that is still open.
  if ((isProjectOwner || userRole === 'superadmin') &&
      status !== 'DELIVERED' && status !== 'CANCELLED' && status !== 'COMPLETED' && status !== 'FINAL_APPROVAL') {
    allowedActions.canCancel = true;
  }

  // Revision requests only under the limit, and only while a cut is in review.
  if ((isProjectOwner || isAssignedEditor) && (status === 'CLIENT_REVIEW' || status === 'DRAFT_READY')) {
    allowedActions.canRequestRevision = revisionCount < maxRevisions;
  }

  return allowedActions;
};

// ---- Filters, search, sorting ----------------------------------------------------

export type ProjectFilter = 'all' | StatusGroup;

export const PROJECT_FILTERS: Array<{ value: ProjectFilter; label: string; empty: string }> = [
  { value: 'all', label: 'All', empty: 'No projects yet' },
  { value: 'needs-review', label: 'Needs review', empty: 'Nothing needs your review' },
  { value: 'in-progress', label: 'In progress', empty: 'No projects in progress' },
  { value: 'waiting', label: 'Waiting for an editor', empty: 'No projects waiting for an editor' },
  { value: 'done', label: 'Delivered', empty: 'No delivered projects yet' },
  { value: 'cancelled', label: 'Cancelled', empty: 'No cancelled projects' },
];

/** The filter named in the URL (?status=needs-review), or All. */
export const parseFilter = (value: string | null | undefined): ProjectFilter =>
  PROJECT_FILTERS.some((filter) => filter.value === value) ? (value as ProjectFilter) : 'all';

export const matchesFilter = (project: ProjectObject, filter: ProjectFilter): boolean =>
  filter === 'all' || getStatusGroup(project.status) === filter;

/** Search the project name, the video name and the editor. */
export const matchesSearch = (project: ProjectObject, term: string): boolean => {
  const query = term.trim().toLowerCase();
  if (!query) return true;
  const editor = projectEditor(project);
  return [project.projectName, project.videoName, editor.name, editor.email]
    .some((value) => (value || '').toLowerCase().includes(query));
};

const timeOf = (value?: string) => {
  const time = value ? Date.parse(value) : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/** Newest first, by creation date. */
export const compareNewestFirst = (a: ProjectObject, b: ProjectObject): number =>
  timeOf(b.createdAt) - timeOf(a.createdAt);

export const visibleProjects = (projects: ProjectObject[], filter: ProjectFilter, term: string): ProjectObject[] =>
  projects
    .filter((project) => Boolean(project) && matchesFilter(project, filter) && matchesSearch(project, term))
    .sort(compareNewestFirst);

export const countByFilter = (projects: ProjectObject[]): Record<ProjectFilter, number> => {
  const counts: Record<ProjectFilter, number> = {
    all: 0,
    'needs-review': 0,
    'in-progress': 0,
    waiting: 0,
    done: 0,
    cancelled: 0,
  };
  projects.forEach((project) => {
    if (!project) return;
    counts.all += 1;
    const group = getStatusGroup(project.status);
    if (group) counts[group] += 1;
  });
  return counts;
};

// ---- Display helpers ----------------------------------------------------------------

/** "1 of 5", "0 of unlimited"; `atLimit` when no revisions are left. */
export const revisionUsage = (project: ProjectObject) => {
  const count = typeof project.revisionCount === 'number' ? project.revisionCount : 0;
  const limit = resolveRevisionLimit(project.revisionLimit);
  return {
    count,
    limit,
    label: `${count} of ${formatRevisionLimit(limit)}`,
    atLimit: count >= limit,
  };
};

/**
 * The server's own explanation when there is one; the fallback for transport
 * failures and masked errors, which mean nothing to the person reading them.
 */
export const friendlyError = (error: unknown, fallback: string): string => {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!message || /failed to fetch|network|status code|unexpected error|internal server/i.test(message)) {
    return fallback;
  }
  return message;
};
